const STORAGE_KEY = "hrms_notifications_v1";
const UNREAD_COUNT_KEY = "hrms_unread_count_v1";

const readStorage = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch (e) {
    console.error("Failed to read notifications from storage", e);
    return [];
  }
};

const getNotificationIdentifier = (item) => {
  return (
    item?.id ??
    item?.data?.id ??
    item?.data?.notification_id ??
    item?.data?.notificationId ??
    item?.notification_id ??
    item?.notificationId
  );
};

const normalizeNotification = (payload) => {
  const identifier = getNotificationIdentifier(payload);
  const id = identifier ?? Date.now() + Math.floor(Math.random() * 1000);
  const title = payload?.title ?? payload?.notification?.title ?? payload?.data?.title ?? payload?.data?.message ?? "";
  const body = payload?.body ?? payload?.message ?? payload?.notification?.body ?? payload?.data?.body ?? payload?.data?.description ?? "";
  const data = payload?.data || payload?.payload || {};
  const read = payload?.is_read ?? payload?.read ?? payload?.data?.is_read ?? false;
  const createdAt = payload?.created_at ?? payload?.createdAt ?? payload?.receivedAt ?? new Date().toISOString();

  return {
    id,
    title,
    body,
    data,
    read,
    createdAt,
    receivedAt: payload?.receivedAt ?? createdAt,
  };
};

const isSameNotification = (existing, candidate) => {
  const existingId = getNotificationIdentifier(existing);
  const candidateId = getNotificationIdentifier(candidate);

  if (existingId && candidateId && String(existingId) === String(candidateId)) {
    return true;
  }

  const existingSignature = [existing?.title, existing?.body, existing?.data?.title, existing?.data?.body, existing?.data?.message, existing?.data?.description]
    .filter(Boolean)
    .join("|");
  const candidateSignature = [candidate?.title, candidate?.body, candidate?.data?.title, candidate?.data?.body, candidate?.data?.message, candidate?.data?.description]
    .filter(Boolean)
    .join("|");

  return Boolean(existingSignature && existingSignature === candidateSignature);
};

const writeStorage = (items) => {
  try {
    const normalizedItems = Array.isArray(items) ? items : [];
    const normalizedPayload = normalizedItems.map((item) => normalizeNotification(item));
    localStorage.setItem(STORAGE_KEY, JSON.stringify(normalizedPayload));
    setUnreadCount(normalizedPayload.filter((item) => !item.read).length);
    window.dispatchEvent(new CustomEvent("notificationsUpdated", { detail: normalizedPayload }));
  } catch (e) {
    console.error("Failed to write notifications to storage", e);
  }
};

const setUnreadCount = (count) => {
  try {
    localStorage.setItem(UNREAD_COUNT_KEY, count.toString());
    window.dispatchEvent(new CustomEvent("unreadCountUpdated", { detail: count }));
  } catch (e) {
    console.error("Failed to write unread count to storage", e);
  }
};

const getUnreadCount = () => {
  try {
    const count = localStorage.getItem(UNREAD_COUNT_KEY);
    return count ? parseInt(count, 10) : 0;
  } catch (e) {
    console.error("Failed to read unread count from storage", e);
    return 0;
  }
};

const getNotifications = () => readStorage();

const replaceNotifications = (payload) => {
  const incomingItems = Array.isArray(payload) ? payload : payload ? [payload] : [];
  const normalizedItems = incomingItems.map((item) => normalizeNotification(item));
  writeStorage(normalizedItems);
  return normalizedItems;
};

const addNotification = (payload) => {
  try {
    const incomingItems = Array.isArray(payload) ? payload : [payload];
    const items = readStorage();

    incomingItems.forEach((entry) => {
      const item = normalizeNotification(entry);
      const existingIndex = items.findIndex((existing) => isSameNotification(existing, item));

      if (existingIndex !== -1) {
        const existingItem = items[existingIndex];
        const mergedItem = {
          ...existingItem,
          ...item,
          data: { ...(existingItem?.data || {}), ...(item?.data || {}) },
          read: existingItem?.read ?? item.read,
          receivedAt: existingItem?.receivedAt || item.receivedAt,
        };
        items[existingIndex] = mergedItem;
      } else {
        items.unshift(item);
      }
    });

    writeStorage(items);
    if (incomingItems.length === 1) {
      window.dispatchEvent(new CustomEvent("newNotification", { detail: items[0] }));
    }
    return items;
  } catch (e) {
    console.error("Failed to add notification", e);
    return [];
  }
};

const markAsRead = (id) => {
  const items = readStorage();
  const idx = items.findIndex((item) => String(getNotificationIdentifier(item)) === String(id));
  if (idx !== -1) {
    items[idx] = { ...items[idx], read: true, is_read: true };
    writeStorage(items);
  }
};

const markAllAsRead = () => {
  const items = readStorage().map((item) => ({ ...item, read: true, is_read: true }));
  writeStorage(items);
};

const clearNotifications = () => {
  writeStorage([]);
};

export { getNotificationIdentifier, normalizeNotification };

export default {
  getNotifications,
  addNotification,
  replaceNotifications,
  markAsRead,
  markAllAsRead,
  clearNotifications,
  setUnreadCount,
  getUnreadCount,
};
